import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
} from "@mui/material";
import { TEXT, tableRowsSx } from "@/controls/section-panel/SectionPanel";
import type { CampaignData } from "../models/CampaignData";
import { COLORS } from "../models/constants";

interface SpeedTemperatureTableProps {
	campaign: CampaignData | null;
}

interface RowConfig {
	label: string;
	actual: number | null;
	recipe: number | null;
	isOutOfRange: boolean;
	format?: (val: number) => string;
}

const cellSx = {
	py: 0,
	px: 1.5,
	height: 28,
	whiteSpace: "nowrap" as const,
	...TEXT.body,
};

const headerCellSx = {
	...cellSx,
	height: 28,
	...TEXT.head,
	color: "text.secondary",
};

const labelCellSx = {
	...cellSx,
	...TEXT.label,
	color: "text.primary",
};

const valueCellSx = {
	...cellSx,
	textAlign: "right" as const,
	fontVariantNumeric: "tabular-nums",
};

export const SpeedTemperatureTable = ({
	campaign,
}: SpeedTemperatureTableProps) => {
	const enableComp = campaign?.EnableComparison ?? false;

	const fmt0 = (v: number) => String(Math.round(v));
	const fmt2 = (v: number) => v.toFixed(2);

	const rows: RowConfig[] = [
		{
			label: "Start Temperature",
			actual: campaign?.StartTemperatureActual ?? null,
			recipe: campaign?.StartTemperature ?? null,
			isOutOfRange:
				enableComp &&
				campaign?.StartTemperature != null &&
				campaign?.StartTemperatureActual != null &&
				campaign.StartTemperatureActual <= campaign.StartTemperature,
			format: fmt0,
		},
		{
			label: "End Temperature",
			actual: campaign?.EndTemperatureActual ?? null,
			recipe: campaign?.EndTemperature ?? null,
			isOutOfRange:
				enableComp &&
				campaign?.EndTemperature != null &&
				campaign?.EndTemperatureActual != null &&
				campaign.EndTemperatureActual <= campaign.EndTemperature,
			format: fmt0,
		},
		{
			label: "Line Speed (ft/min)",
			actual: campaign?.LineSpeedActual ?? null,
			recipe: campaign?.LineSpeed ?? null,
			isOutOfRange:
				enableComp &&
				campaign?.LineSpeed != null &&
				campaign?.LineSpeedActual != null &&
				campaign.LineSpeedActual <= campaign.LineSpeed,
			format: fmt0,
		},
		{
			label: "Gap S1",
			actual: campaign?.Gap ?? null,
			recipe: campaign?.GapGoal ?? null,
			isOutOfRange:
				enableComp &&
				campaign?.GapGoal != null &&
				campaign?.Gap != null &&
				campaign.Gap >= campaign.GapGoal,
			format: fmt2,
		},
		{
			label: "Gap S17",
			actual: campaign?.Gap17 ?? null,
			recipe: campaign?.Gap17Goal ?? null,
			isOutOfRange:
				enableComp &&
				campaign?.Gap17Goal != null &&
				campaign?.Gap17 != null &&
				campaign.Gap17 >= campaign.Gap17Goal,
			format: fmt2,
		},
	];

	return (
		<Table size="small" sx={tableRowsSx}>
			<TableHead>
				<TableRow>
					<TableCell sx={headerCellSx} />
					<TableCell sx={{ ...headerCellSx, textAlign: "right" }}>
						Actual
					</TableCell>
					<TableCell sx={{ ...headerCellSx, textAlign: "right" }}>
						Recipe
					</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{rows.map((row) => (
					<TableRow key={row.label}>
						<TableCell sx={labelCellSx}>{row.label}</TableCell>
						<TableCell
							sx={{
								...valueCellSx,
								fontWeight: 600,
								...(row.isOutOfRange
									? { color: COLORS.outOfRange, fontWeight: "bold" }
									: {}),
							}}
						>
							{row.actual != null ? (row.format ?? fmt0)(row.actual) : "-"}
						</TableCell>
						<TableCell sx={{ ...valueCellSx, color: "text.secondary" }}>
							{row.recipe != null ? (row.format ?? fmt0)(row.recipe) : "-"}
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	);
};
